import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, DataTableAction, DataTableHeader, DataTableRow } from "../../components/DataTable/DataTable";
import { Filter, FilterBaseData } from "../../components/Filter/Filter";
import { FormFieldOptions } from "../../components/GeneratedForm/GeneratedForm";
import { Pagination } from "../../components/Pagination/Pagination";
import { View } from "../../components/View/View";
import { useUrlParams } from "../../hooks/useUrlParams";
import { AdminViewParams, ADMIN_VIEW_PATH } from "../../routes";
import { ConditionModeEnum, MatchModeEnum, SortingInput, useConclusionOptionsQuery, UserScopeEnum } from "../../schema";
import { buildUrl } from "../../services/buildUrl";
import { getPageCount } from "../../services/getPageCount";
import { getSkipTake } from "../../services/getSkipTake";
import { getSortDirectionFromParams } from "../../services/getSortDirectionFromParams";
import { getSortOptionsFromSearchParams } from "../../services/getSortOptionsFromSearchParams";
import { getUpdatedSortOptions } from "../../services/getUpdatedSortOptions";
import { getUrlSearchParamsString } from "../../services/getUrlSearchParamsString";

import { AdminViewProps } from "../AdminView/AdminView";
import { ErrorView } from "../ErrorView/ErrorView";

interface ConclusionOptionFilterInputData extends FilterBaseData {
  minClarityScore: string;
  maxClarityScore: string | null;
  version: string;
  text: string;
  sort: SortingInput[] | null;
}

export const ConclusionOptionsListView: React.FC<AdminViewProps> = () => {
  const navigate = useNavigate();

  // parse filter url parameters
  const params = useUrlParams<ConclusionOptionFilterInputData>((params) => ({
    minClarityScore: params.minClarityScore ?? "",
    maxClarityScore: params.maxClarityScore ? params.maxClarityScore : null,
    version: params.version ?? "",
    text: params.text ?? "",
    matchMode: params.matchMode ? (params.matchMode as MatchModeEnum) : MatchModeEnum.STARTS_WITH,
    conditionMode: params.conditionMode ? (params.conditionMode as ConditionModeEnum) : ConditionModeEnum.AND,
    page: params.page ? parseInt(params.page, 10) : 1,
    sort: params.sort ? getSortOptionsFromSearchParams(params.sort) : null,
  }));

  // fetch items
  const { data, loading, error } = useConclusionOptionsQuery({
    variables: {
      filter: {
        minClarityScore: params.minClarityScore ? Number.parseFloat(params.minClarityScore) : undefined,
        maxClarityScore: params.maxClarityScore ? Number.parseFloat(params.maxClarityScore) : undefined,
        version: params.version ? Number.parseInt(params.version, 10) : undefined,
        text: params.text,
      },
      pagination: {
        ...getSkipTake(params.page),
      },
      match: {
        matchMode: params.matchMode,
        conditionMode: params.conditionMode,
      },
      sort: params.sort ?? null,
    },
  });

  // get items
  const conclusionOptions = useMemo(
    () => data?.conclusionOptions.conclusionOptions ?? [],
    [data?.conclusionOptions.conclusionOptions],
  );
  const total = data?.conclusionOptions.total ?? 0;
  const pageCount = getPageCount(total);

  // filters configuration
  const filters = useMemo<FormFieldOptions[]>(
    () => [
      {
        field: "text",
        type: "text",
        name: "minClarityScore",
        label: "Minimum score value",
        defaultValue: params.minClarityScore,
      },
      {
        field: "text",
        type: "text",
        name: "maxClarityScore",
        label: "Maximum score value",
        defaultValue: params.maxClarityScore ?? "",
      },
      {
        field: "text",
        type: "text",
        name: "version",
        label: "Version",
        defaultValue: params.version ?? "",
      },
      {
        field: "text",
        type: "text",
        name: "text",
        label: "Text",
        defaultValue: params.text ?? "",
      },
    ],
    [params.minClarityScore, params.maxClarityScore, params.version, params.text],
  );

  // handle sorting by column
  const onHeaderClick = useCallback(
    (databaseColumnName: string) =>
      navigate({
        pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, { menu: "clarity-score", page: "conclusion-options" }),
        search: getUrlSearchParamsString({ ...params, sort: getUpdatedSortOptions(databaseColumnName, params.sort) }),
      }),
    [navigate, params],
  );

  // data table headers
  const headers = useMemo<DataTableHeader[]>(
    () => [
      {
        label: "Minimum score value",
        onClick: () => onHeaderClick("minClarityScore"),
        sortdirection: getSortDirectionFromParams("minClarityScore", params.sort),
      },
      {
        label: "Maximum score value",
        onClick: () => onHeaderClick("maxClarityScore"),
        sortdirection: getSortDirectionFromParams("maxClarityScore", params.sort),
      },
      {
        label: "version",
        onClick: () => onHeaderClick("version"),
        sortdirection: getSortDirectionFromParams("version", params.sort),
      },
      {
        label: "text",
        // no point to sort this column
      },
    ],
    [onHeaderClick, params.sort],
  );

  // data table rows
  const rows = useMemo<DataTableRow[]>(
    () =>
      conclusionOptions.map((option) => {
        return {
          id: option.id,
          cells: [
            {
              content: option.minClarityScore,
            },
            {
              content: option.maxClarityScore,
            },
            {
              content: option.version,
            },
            {
              content: option.text,
            },
          ],
          actions: [
            {
              label: "View details",
              authorizedScopes: [
                UserScopeEnum.SUPERADMIN,
                UserScopeEnum.CLARITY_TEST,
                UserScopeEnum.CLARITY_TEST_CONCLUSION_OPTION,
              ],
              onClick: (conclusionOptionId) =>
                navigate({
                  pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, {
                    menu: "clarity-score",
                    page: "conclusion-options",
                    id: conclusionOptionId,
                  }),
                }),
            },
            {
              label: "Edit",
              authorizedScopes: [
                UserScopeEnum.SUPERADMIN,
                UserScopeEnum.CLARITY_TEST,
                UserScopeEnum.CLARITY_TEST_CONCLUSION_IMAGE,
                UserScopeEnum.CLARITY_TEST_UPDATE_CONCLUSION_OPTION,
              ],
              onClick: (conclusionOptionId) =>
                navigate({
                  pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, {
                    menu: "clarity-score",
                    page: "conclusion-options",
                    id: conclusionOptionId,
                    modifier: "edit",
                  }),
                }),
            },
          ],
        };
      }),
    [navigate, conclusionOptions],
  );

  // data table bulk actions
  const bulkActions = useMemo<DataTableAction<string[]>[]>(() => [], []);

  // handle filters submit
  const onFilterSubmit = useCallback(
    (data: ConclusionOptionFilterInputData) => {
      navigate({
        pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, { menu: "clarity-score", page: "conclusion-options" }),
        search: getUrlSearchParamsString(data),
      });
    },
    [navigate],
  );

  //   // handle errors
  if (error) {
    return <ErrorView title="Fetching conclusion options failed" error={error} />;
  }

  // render details
  return (
    <View scrollable>
      <Filter
        title="Clarity test conclusion options"
        fields={filters}
        loading={loading}
        matchMode={params.matchMode}
        conditionMode={params.conditionMode}
        onSubmit={onFilterSubmit}
      />
      <DataTable
        headers={headers}
        rows={rows}
        bulkActions={bulkActions}
        loading={loading}
        openAuthorizedScopes={[
          UserScopeEnum.SUPERADMIN,
          UserScopeEnum.CLARITY_TEST,
          UserScopeEnum.CLARITY_TEST_CONCLUSION_OPTION,
        ]}
        onOpen={(row) =>
          navigate({
            pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, {
              menu: "clarity-score",
              page: "conclusion-options",
              modifier: row.id,
            }),
          })
        }
      />
      <Pagination
        sticky
        pageCount={pageCount}
        currentPage={params.page}
        onPageChange={(page) =>
          navigate({
            pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, { menu: "clarity-score", page: "conclusion-options" }),
            search: getUrlSearchParamsString({ ...params, page }),
          })
        }
      />
    </View>
  );
};
